import { Routes, Route, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { useSiteTooling } from './hooks/useSiteTooling';
import { ScrollTracker } from './components/ScrollTracker';
import Navigation from './sections/Navigation';
import Whatsapp from './components/Whatsapp/Whatsapp';
import QuickContact from './components/QuickContact';
import SEOLayout from './components/SEOLayout';
import Home from './pages/Home/Home';
import Products from './pages/Products/Products';
import Racking from './pages/Racking/Racking';
import Shelving from './pages/Shelving/Shelving';
import Mezzanine from './pages/Mezzanine/Mezzanine';
import Storage from './pages/Storage/Storage';
import About from './pages/About/About';
import Capabilities from './pages/Capabilities/Capabilities';
import Projects from './pages/Projects/Projects';
import Contact from './pages/Contact/Contact';
import './App.css';

// Scroll to top and track page view on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  const { trackPageview } = useSiteTooling();
  useEffect(() => {
    window.scrollTo(0, 0);
    trackPageview(pathname);
  }, [pathname, trackPageview]);
  return null;
}

// SEO Wrapper Components
const HomePage = () => (
  <SEOLayout
    title="Industrial Storage Solutions South Africa"
    description="B-BBEE Level 1 certified industrial storage solutions. Pallet racking, shelving, mezzanine floors & lockers for warehouses, retail & manufacturing."
    keywords={['home', 'storage solutions', 'warehouse storage']}
  >
    <Home />
  </SEOLayout>
);

const ProductsPage = () => (
  <SEOLayout
    title="Industrial Storage Products"
    description="Browse our range of pallet racking, industrial shelving, mezzanine floors & storage lockers. SABS certified solutions for South African businesses."
    keywords={['products', 'pallet racking', 'industrial shelving', 'mezzanine floors']}
  >
    <Products />
  </SEOLayout>
);

const RackingPage = () => (
  <SEOLayout
    title="Pallet Racking Systems"
    description="Heavy-duty pallet racking systems with load capacity up to 4,000kg per level. Selective, drive-in, cantilever & push-back configurations. SABS certified."
    keywords={['pallet racking', 'warehouse racking', 'heavy duty racking', 'storage racks']}
    image="/racking_aisle.jpg"
  >
    <Racking />
  </SEOLayout>
);

const ShelvingPage = () => (
  <SEOLayout
    title="Industrial Shelving"
    description="Heavy-duty industrial shelving for warehouses, archives, retail & workshops. Bolted or boltless options, steel/chrome/plastic finishes."
    keywords={['industrial shelving', 'warehouse shelving', 'metal shelving', 'storage shelving']}
    image="/shelving_bay.jpg"
  >
    <Shelving />
  </SEOLayout>
);

const MezzaninePage = () => (
  <SEOLayout
    title="Mezzanine Floors"
    description="Custom mezzanine floors to maximize your vertical space. Building control approved designs. Load capacity 300kg-1,000kg/m²."
    keywords={['mezzanine floors', 'mezzanine levels', 'warehouse mezzanine', 'storage platforms']}
    image="/mezzanine_install.jpg"
  >
    <Mezzanine />
  </SEOLayout>
);

const StoragePage = () => (
  <SEOLayout
    title="Lockers & Storage"
    description="Secure lockers, cabinets & small-parts storage systems. Steel lockers with ventilation, digital/key lock options."
    keywords={['storage lockers', 'industrial lockers', 'storage cabinets', 'small parts storage']}
    image="/lockers_storage.jpg"
  >
    <Storage />
  </SEOLayout>
);

const AboutPage = () => (
  <SEOLayout
    title="About Us"
    description="Learn about Molomo NR - a B-BBEE Level 1 certified company providing industrial storage solutions since 2018. 100% black-owned South African business."
    keywords={['about', 'company', 'B-BBEE Level 1', 'black owned', 'South African company']}
  >
    <About />
  </SEOLayout>
);

const CapabilitiesPage = () => (
  <SEOLayout
    title="Our Capabilities"
    description="End-to-end industrial storage services: site survey, CAD design, manufacture & installation. Nationwide delivery across South Africa."
    keywords={['capabilities', 'services', 'site survey', 'CAD design', 'installation']}
  >
    <Capabilities />
  </SEOLayout>
);

const ProjectsPage = () => (
  <SEOLayout
    title="Our Projects"
    description="View our completed projects. 100+ installations across South Africa including warehouses, retail facilities & manufacturing plants."
    keywords={['projects', 'portfolio', 'installations', 'completed work']}
  >
    <Projects />
  </SEOLayout>
);

const ContactPage = () => (
  <SEOLayout
    title="Contact Us"
    description="Get in touch for a free quote. Call +27 82 971 3299 or email nhlapolindiwe4@gmail.com. Serving Sebokeng, Gauteng & nationwide."
    keywords={['contact', 'quote', 'get in touch', 'phone', 'email', 'Sebokeng']}
  >
    <Contact />
  </SEOLayout>
);

function App() {
  return (
    <HelmetProvider>
      <div className="relative">
        {/* Grain overlay */}
        <div className="grain-overlay" />
        
        {/* Navigation */}
        <Navigation />
        
        {/* Scroll to top and analytics on page change */}
        <ScrollToTop />
        <ScrollTracker />
        
        {/* Routes */}
        <main className="relative">
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/products" element={<ProductsPage />} />
            <Route path="/products/racking" element={<RackingPage />} />
            <Route path="/products/shelving" element={<ShelvingPage />} />
            <Route path="/products/mezzanine" element={<MezzaninePage />} />
            <Route path="/products/storage" element={<StoragePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/capabilities" element={<CapabilitiesPage />} />
            <Route path="/projects" element={<ProjectsPage />} />
            <Route path="/contact" element={<ContactPage />} />
          </Routes>
        </main>

        {/* Floating WhatsApp Button */}
        <Whatsapp />

        {/* Quick Contact Widget */}
        <QuickContact />
      </div>
    </HelmetProvider>
  );
}

export default App;
